<script>
  import { onMount } from 'svelte';
  import { nav, go } from './lib/nav.svelte.js';
  import { role, restore, mayVisit, applyContext } from './lib/role.svelte.js';
  import { getHealth, getContext } from './lib/api.js';
  import Header from './components/Header.svelte';

  import Login from './routes/Login.svelte';
  import Dashboard from './routes/Dashboard.svelte';
  import ConnectorConfigure from './routes/ConnectorConfigure.svelte';
  import ConnectorRun from './routes/ConnectorRun.svelte';
  import Records from './routes/Records.svelte';
  import Partners from './routes/Partners.svelte';
  import PartnerDetails from './routes/PartnerDetails.svelte';
  import PartnerAccess from './routes/PartnerAccess.svelte';
  import PartnerConfirm from './routes/PartnerConfirm.svelte';
  import Catalog from './routes/Catalog.svelte';
  import Queries from './routes/Queries.svelte';
  import Analysis from './routes/Analysis.svelte';
  import Disclosure from './routes/Disclosure.svelte';
  import DisclosureLabel from './routes/DisclosureLabel.svelte';
  import DisclosureAggregates from './routes/DisclosureAggregates.svelte';
  import DisclosureConfirm from './routes/DisclosureConfirm.svelte';

  const ROUTES = {
    login: Login,
    dashboard: Dashboard,
    connector: ConnectorConfigure,
    'connector/run': ConnectorRun,
    records: Records,
    partners: Partners,
    partner: PartnerDetails,
    'partner/access': PartnerAccess,
    'partner/confirm': PartnerConfirm,
    catalog: Catalog,
    queries: Queries,
    analysis: Analysis,
    disclosure: Disclosure,
    // Assessing and labelling are one screen. The old route is kept pointing at
    // it so existing links and bookmarks still land somewhere sensible.
    'disclosure/assess': DisclosureLabel,
    'disclosure/label': DisclosureLabel,
    'disclosure/aggregates': DisclosureAggregates,
    'disclosure/confirm': DisclosureConfirm,
  };

  let ready = $state(false);

  onMount(async () => {
    restore();
    // Check the backend's held credential once at boot, so a stopped or
    // misconfigured API is reported on the sign-in screen rather than as a
    // confusing failure inside whatever screen happens to load first. Load the
    // Pod-resident presentation identity in the same round (brand, tagline,
    // title, accent) — a missing file or a down backend falls back gracefully.
    const [health, context] = await Promise.allSettled([getHealth(), getContext()]);
    if (health.status === 'fulfilled') role.health = health.value;
    else role.error = health.reason.message;
    applyContext(context.status === 'fulfilled' ? context.value : null);
    ready = true;
  });

  // Role gate: everything except the sign-in screen needs a chosen role, and
  // each role only reaches its own screens (roles.md).
  const Current = $derived.by(() => {
    if (!ready) return null;
    if (!role.current && nav.route !== 'login') {
      go('login');
      return Login;
    }
    if (role.current && nav.route === 'login') {
      go('dashboard');
      return Dashboard;
    }
    if (role.current && !mayVisit(nav.route)) {
      go('dashboard');
      return Dashboard;
    }
    return ROUTES[nav.route] ?? Dashboard;
  });
</script>

<div class="wf2-page">
  <div class="wf2-screen">
    <Header />
    {#if !ready}
      <div class="state"><span class="spinner"></span>Connecting to the app backend…</div>
    {:else}
      <Current />
    {/if}
  </div>
</div>
